// Asking the dev Mac over its wire link — kind `devcode` (docs/map.md §8, docs/features/dev-mode.md):
//   page → Mac   {what: 'hello'} · {what: 'manifest', id} · {what: 'get', id, path} · {what: 'bye'}
//   Mac → page   {what: 'offer', mac, source: 'app' | 'folder', version, folder, live}
//                {what: 'manifest', id, manifest, signature} — the list as JSON text, signed (gate.js)
//                {what: 'changed', paths, all}
//                a file: 4 bytes (big-endian) header length · JSON header {what: 'file', id, path, status,
//                contentType, reason} · the file's bytes — the only answer that is not plain JSON, so a file
//                travels as itself rather than as base64 (a header under 16 MB always starts with a 0 byte, JSON
//                with '{').
const dec = new TextDecoder()
let seq = 0
const waits = new Map()   // id → resolve

/// One message from the Mac, read: { message, body } (body only for a file).
export function hear(payload) {
  const bytes = payload instanceof Uint8Array ? payload : new Uint8Array(payload)
  try {
    if (bytes[0] === 0x7b) return { message: JSON.parse(dec.decode(bytes)), body: null }
    const length = new DataView(bytes.buffer, bytes.byteOffset, 4).getUint32(0)
    return { message: JSON.parse(dec.decode(bytes.subarray(4, 4 + length))), body: bytes.subarray(4 + length) }
  } catch { return { message: null, body: null } }
}

/// An answer to something asked here: hands it to whoever is waiting. True when it was one.
export function answered(message, body) {
  const resolve = message && waits.get(message.id)
  if (!resolve) return false
  waits.delete(message.id)
  resolve({ message, body })
  return true
}

/// Ask the Mac on `link` (a MacWindows): resolves with { message, body }, or rejects after `wait` ms.
export function ask(link, message, wait = 30000) {
  const id = 'd' + (++seq)
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => { waits.delete(id); reject(new Error('the Mac did not answer')) }, wait)
    waits.set(id, (reply) => { clearTimeout(timer); resolve(reply) })
    link.send('devcode', { ...message, id })
  })
}

/// One file of the dev copy: { status, contentType, body (ArrayBuffer), reason }.
export async function getFile(link, path) {
  const { message, body } = await ask(link, { what: 'get', path })
  const copy = body ? body.slice().buffer : new ArrayBuffer(0)
  return { status: message.status | 0, contentType: message.contentType || 'application/octet-stream', body: copy, reason: message.reason || '' }
}

/// The dev copy's file list as the Mac signed it: { text, signature } — read only through gate.js `verifyManifest`.
export async function getManifest(link) {
  const { message } = await ask(link, { what: 'manifest' })
  if (typeof message?.manifest !== 'string') throw new Error(message?.reason || 'the Mac sent no list of files')
  return { text: message.manifest, signature: message.signature || '' }
}
